import SearchIcon from '@mui/icons-material/Search';
import {
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Door } from 'graphql/generated/graphqlTypes';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { orderBy, upperFirst } from 'lodash';
import React, { useMemo, useState } from 'react';
import { getLockTypeName } from 'utils/Lock/Lock';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { useSearch } from 'v2/hooks/useSearch/useSearch';

const searchKeys = ['name'];
const ROW_HEIGHT = 44;

type SortOption = 'name_asc' | 'name_desc' | 'selected' | 'type' | 'serial_number';

const sortDoors = (doors: Door[], sortBy: SortOption, selectedIdSet: Set<string>, translation: Translation): Door[] => {
  switch (sortBy) {
    case 'name_asc':
      return orderBy(doors, [(door) => door.name.toLowerCase()], ['asc']);
    case 'name_desc':
      return orderBy(doors, [(door) => door.name.toLowerCase()], ['desc']);
    case 'selected':
      return orderBy(
        doors,
        [(door) => (selectedIdSet.has(door.id) ? 0 : 1), (door) => door.name.toLowerCase()],
        ['asc', 'asc']
      );
    case 'type':
      return orderBy(
        doors,
        [(door) => getLockTypeName(door.type, translation) ?? '', (door) => door.name.toLowerCase()],
        ['asc', 'asc']
      );
    case 'serial_number':
      return orderBy(doors, [(door) => door.hardwareIdentifier.toLowerCase()], ['asc']);
  }
};

interface Props {
  currentDoorIds: string[];
  allDoors: Door[];
  onSave: (doorIds: string[]) => Promise<{ success: boolean; errorMessage?: string }>;
}

const AddDevicesDrawer: React.FC<Props> = ({ currentDoorIds, allDoors, onSave }) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>(currentDoorIds);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('name_asc');
  const [error, setError] = useState<string>();

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredDoors = useSearch({ data: allDoors, search, keys: searchKeys });
  const sortedDoors = useMemo(
    () => sortDoors(filteredDoors, sortBy, selectedIdSet, translation),
    [filteredDoors, sortBy, selectedIdSet, translation]
  );

  const handleToggle = (doorId: string) => {
    setSelectedIds((prev) => (prev.includes(doorId) ? prev.filter((id) => id !== doorId) : [...prev, doorId]));
  };

  const filteredDoorIds = useMemo(() => filteredDoors.map((door) => door.id), [filteredDoors]);
  const isAllFilteredSelected = filteredDoorIds.length > 0 && filteredDoorIds.every((id) => selectedIdSet.has(id));

  const handleToggleAll = () => {
    setSelectedIds((prev) =>
      isAllFilteredSelected
        ? prev.filter((id) => !filteredDoorIds.includes(id))
        : [...prev, ...filteredDoorIds.filter((id) => !prev.includes(id))]
    );
  };

  const handleSave = async () => {
    setError(undefined);
    setIsSubmitting(true);
    try {
      const result = await onSave(selectedIds);
      if (!result.success) throw new Error(result.errorMessage);
      showSnackbar(translation.device_group_devices_updated, { severity: 'success' });
      closeDrawer();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : undefined;
      setError(message ?? translation.device_group_error_updating_devices);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.edit_devices}
      primaryAction={{
        id: 'save-button',
        label: translation.save,
        onClick: handleSave,
        loading: isSubmitting
      }}
      secondaryAction={{ id: 'cancel-button', label: translation.cancel }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack spacing={2}>
        <TextField
          id="drawer-search-input"
          variant="outlined"
          size="small"
          fullWidth
          placeholder={translation.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel id="sort-select-label">{translation.sort}</InputLabel>
          <Select
            labelId="sort-select-label"
            id="sort-select"
            value={sortBy}
            label={translation.sort}
            onChange={(event) => setSortBy(event.target.value as SortOption)}
          >
            <MenuItem value="name_asc">{translation.a_z}</MenuItem>
            <MenuItem value="name_desc">{translation.z_a}</MenuItem>
            <MenuItem value="selected">{upperFirst(translation.selected)}</MenuItem>
            <MenuItem value="type">{translation.device_type}</MenuItem>
            <MenuItem value="serial_number">{translation.serial_number}</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ pr: 2 }}>
          <Typography variant="caption" color="textSecondary">
            {translation.select_all}
          </Typography>
          <Checkbox
            id="select-all-devices-checkbox"
            checked={isAllFilteredSelected}
            onChange={handleToggleAll}
            disableRipple
          />
        </Stack>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sortedDoors.map((door) => {
            const isChecked = selectedIdSet.has(door.id);
            return (
              <ListItem
                key={door.id}
                disablePadding
                sx={{ backgroundColor: (theme) => theme.custom.background.level2, borderRadius: 1 }}
              >
                <ListItemButton onClick={() => handleToggle(door.id)} sx={{ borderRadius: 1, py: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} minWidth={0}>
                    <LockIconNameCell door={door} height={ROW_HEIGHT} />
                    <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0, ml: 1 }}>
                      {door.hardwareIdentifier}
                    </Typography>
                  </Stack>
                  <Checkbox
                    id={`device-${door.id}-checkbox`}
                    edge="start"
                    checked={isChecked}
                    disableRipple
                    tabIndex={-1}
                    sx={{ marginLeft: '.5em' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Stack>
    </DrawerTemplate>
  );
};

export default AddDevicesDrawer;
